/**
 * Travel vibes — what a traveller is in the mood for, as opposed to what we
 * happen to file something under.
 *
 * Asked once during sign-up, then used to reorder Home and Discover. Vibes are
 * deliberately *not* catalogue categories: "Pools" is a product type, "Chill &
 * spa" is a reason to travel. One vibe spans several categories, and a venue
 * can answer to several vibes.
 *
 * Matching is rule-based on purpose — every rule below is a slug, segment or
 * tag that already exists on rows today, so this ships without an editorial
 * backlog and can be retuned by editing this file alone. Nothing is ever
 * hidden by a vibe; they only reorder.
 */

export const VIBES = [
  "adventure",
  "chill-spa",
  "foodie",
  "culture",
  "nightlife",
  "beach-water",
  "desert",
  "family",
  "romantic",
] as const;

export type Vibe = (typeof VIBES)[number];

export function isVibe(value: string): value is Vibe {
  return (VIBES as readonly string[]).includes(value);
}

/** Drops anything that isn't a known vibe — user input and old rows both pass through here. */
export function parseVibes(input: readonly string[] | null | undefined): Vibe[] {
  if (!input) return [];
  const seen = new Set<Vibe>();
  for (const value of input) if (isVibe(value)) seen.add(value);
  return [...seen];
}

/**
 * What each vibe pulls in. All three axes are optional and additive — a row
 * matches a vibe if it matches on ANY axis.
 *
 * - `categorySlugs` — `categories.slug`
 * - `segments` — `activities.segment`
 * - `tags` — `activities.tags` (the same keys Discover's sub-filters use)
 */
export interface VibeRule {
  categorySlugs?: readonly string[];
  segments?: readonly string[];
  tags?: readonly string[];
}

export const VIBE_RULES: Record<Vibe, VibeRule> = {
  adventure: {
    categorySlugs: [
      "quad", "buggy", "jet-ski", "diving", "air", "horse",
      "excursions", "day-trips", "water", "trips",
    ],
  },
  "chill-spa": {
    categorySlugs: ["spas", "pools"],
    segments: ["spas"],
  },
  foodie: {
    categorySlugs: ["restaurants", "cooking-class"],
    segments: ["restaurants"],
    tags: ["fine-dining", "seafood", "street-food", "cafe"],
  },
  culture: {
    categorySlugs: [
      "city-tour", "crafts", "pottery", "weaving",
      "zellige", "calligraphy-painting", "shows",
    ],
    segments: ["locations"],
  },
  nightlife: {
    categorySlugs: ["shows"],
    tags: ["nightclub", "bar", "rooftop", "lounge", "live-music"],
  },
  "beach-water": {
    categorySlugs: ["boats", "diving", "jet-ski", "water"],
    segments: ["beach-clubs"],
  },
  desert: {
    categorySlugs: ["camel", "camp", "quad", "day-trips"],
  },
  family: {
    categorySlugs: ["pools", "horse", "camel", "crafts", "pottery", "cooking-class"],
  },
  romantic: {
    categorySlugs: ["villa", "spas", "air", "camp"],
    tags: ["rooftop", "fine-dining"],
  },
};

/** The shape any rankable row needs to expose. All fields optional — rows vary. */
export interface VibeMatchable {
  categorySlug?: string | null;
  segment?: string | null;
  tags?: readonly string[] | null;
}

/** How many of the picked vibes a row answers to. 0 means no match. */
export function vibeScore(row: VibeMatchable, vibes: readonly Vibe[]): number {
  if (vibes.length === 0) return 0;
  let score = 0;
  for (const vibe of vibes) {
    const rule = VIBE_RULES[vibe];
    const hit =
      (!!row.categorySlug && !!rule.categorySlugs?.includes(row.categorySlug)) ||
      (!!row.segment && !!rule.segments?.includes(row.segment)) ||
      (!!row.tags?.length && !!rule.tags?.some((t) => row.tags!.includes(t)));
    if (hit) score += 1;
  }
  return score;
}

/**
 * Stable reorder: rows matching more of the picked vibes come first, and rows
 * with equal score keep their original order. A stable partition rather than a
 * comparator — Array.sort stability differs across engines and Hermes is not
 * the one our tests run on.
 *
 * Nothing is dropped: unmatched rows follow, untouched.
 */
export function sortByVibes<T extends VibeMatchable>(
  rows: readonly T[],
  vibes: readonly Vibe[],
): T[] {
  if (vibes.length === 0 || rows.length === 0) return [...rows];

  const buckets = new Map<number, T[]>();
  for (const row of rows) {
    const score = vibeScore(row, vibes);
    const bucket = buckets.get(score);
    if (bucket) bucket.push(row);
    else buckets.set(score, [row]);
  }

  return [...buckets.keys()]
    .sort((a, b) => b - a)
    .flatMap((score) => buckets.get(score)!);
}
